import React, { useState, useMemo, useCallback } from 'react';
import { Menu, MenuItem, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { TimetrackerStyled } from './time-tracker.styles';
import { STEPS_OPTIONS_TIME_TRACKER } from './time-tracker.utils';
import TimeSheet from './time-sheet/time-sheet';
import useUsers from '../../hooks/useUsers'; // Import useUsers hook
import TableActionMenu from '../../components/table-action-menu/table-action-menu';
import ExportCsvModal from './export-csv-modal/export-csv-modal';
import { downloadTimeSheetCsv } from './export-csv-modal/csv-utils/timesheet-csv-utils';
import { downloadOvertimeCsv } from './export-csv-modal/csv-utils/overtime.utils';
import { downloadProjectCostCSV } from './export-csv-modal/csv-utils/project-cost-utils';
import { Typography } from '@mui/material';
import { UpDownArrowSvg } from '../../utils/svg-components';
import { isSomething } from '../../utils/utils';

const TimeTracker = () => {
  const navigate = useNavigate();
  const { currentUser, userDetails, userPermissions } = useAuth();

  // State for UI control
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openCsvModal, setOpenCsvModal] = useState(false);

  const [activeStep, setActiveStep] = useState(STEPS_OPTIONS_TIME_TRACKER[1]?.value);

  // Use custom hook to fetch all users
  const { data: allUsers = [], isLoading } = useUsers();

  const isPrivilegedUser = useMemo(
    () => ['Admin', 'Supervisor', 'Project Manager'].includes(userPermissions),
    [userPermissions],
  );

  // Memoized list of viewable users based on user role and employment status
  const viewableUsers = useMemo(() => {
    if (!userDetails || allUsers.length === 0) return [];

    const userEmail = currentUser.email;
    let filteredUsers = [];

    if (isPrivilegedUser) {
      filteredUsers = allUsers.filter(
        (user) =>
          user.email !== userEmail && !['Inactive'].includes(user.employmentStatus),
      );
    }

    return filteredUsers;
  }, [allUsers, userDetails, isPrivilegedUser, , currentUser.email]);

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleUserSelection = useCallback(
    (user) => {
      if (user.email === currentUser.email) {
        setSelectedUser(null);
      } else {
        setSelectedUser(user);
      }
      handleClose();
    },
    [currentUser.email, handleClose],
  );

  const handleExport = useCallback(async (type, startDate, endDate, project) => {
    if (type === 'overtime') {
      await downloadOvertimeCsv(startDate, endDate);
    } else if (type === 'cost') {
      await downloadProjectCostCSV(startDate, endDate, project);
    } else if (type === 'timesheet') {
      await downloadTimeSheetCsv(startDate, endDate, project);
    }
  }, []);

  if (isLoading) return <Typography variant="h5">Loading...</Typography>;

  return (
    <TimetrackerStyled>
      <div className="header">
        <div className="info">
          <div className="viewing">
            <Typography variant="h5">
              Viewing:{' '}
              {selectedUser
                ? `${selectedUser.firstName} ${selectedUser.surname}`
                : userDetails.fullName}
            </Typography>
            {isPrivilegedUser && (
              <IconButton onClick={handleClick}>
                <UpDownArrowSvg />
              </IconButton>
            )}
          </div>
          {(isSomething(viewableUsers) || selectedUser) && (
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {/* Show current user as an option if another user is currently selected */}
              {selectedUser && (
                <MenuItem key={currentUser.email} onClick={() => handleUserSelection(currentUser)}>
                  {userDetails.fullName}
                </MenuItem>
              )}
              {viewableUsers.map((user) => (
                <MenuItem key={user.email} onClick={() => handleUserSelection(user)}>
                  {`${user.firstName} ${user.surname}`}
                </MenuItem>
              ))}
            </Menu>
          )}

          <div className="viewing">
            <Typography variant="h5">
              Today is:{' '}
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>

            {isPrivilegedUser && (
              <TableActionMenu
                noBorder
                width={'200px'}
                options={[
                  {
                    title: `Export Timesheet CSV`,
                    value: 'download-csv',
                    onClick: () => setOpenCsvModal(true),
                  },
                ]}
              />
            )}
          </div>
        </div>
      </div>

      <div className="timesheet">
        <TimeSheet
          selectedUser={selectedUser}
          currentUser={currentUser}
          userPermissions={userPermissions}
          allUsers={allUsers}
        />
      </div>
      {openCsvModal && (
        <ExportCsvModal onClose={() => setOpenCsvModal(false)} onExport={handleExport} />
      )}
    </TimetrackerStyled>
  );
};

export default TimeTracker;
